import { useState, type InputHTMLAttributes } from 'react';

/**
 * A password box with a way to see what you typed.
 *
 * Every password field in the Hub is one of these, so the control is in the same place and
 * behaves the same way whether somebody is signing in, choosing a new password, or pasting a
 * GitLab token. That last one is the case that makes it worth having: a `glpat-…` token is
 * forty characters of noise nobody can proof-read, and getting it wrong fails later, somewhere
 * else, with a message about permissions.
 *
 * REVEALED IS NEVER THE STARTING STATE, and it resets on every mount. Somebody who reveals a
 * password to check a typo should not find it still legible on the next page, or after coming
 * back to this one — the point is a glance, not a mode you can leave switched on.
 */
export function PasswordField(props: InputHTMLAttributes<HTMLInputElement>) {
  const [shown, setShown] = useState(false);

  return (
    <span className="password-field">
      <input {...props} type={shown ? 'text' : 'password'} />
      <button
        type="button"
        className="password-reveal"
        /**
         * `type="button"`, which matters more than it looks: the default inside a form is
         * `submit`, so without it revealing your password would try to sign you in.
         */
        onClick={() => setShown((was) => !was)}
        aria-pressed={shown}
        aria-label={shown ? 'Hide what you typed' : 'Show what you typed'}
        title={shown ? 'Hide what you typed' : 'Show what you typed'}
        /* Reachable, but after the field itself and never in the way of tabbing to Submit. */
        tabIndex={-1}
      >
        <EyeIcon off={shown} />
      </button>
    </span>
  );
}

/**
 * Open when hidden, struck through when shown — the icon says what pressing it will DO, which
 * is the convention everywhere else and the opposite of what "show the current state" would
 * give you.
 */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.6" />
      {off && <path d="M4 20 20 4" />}
    </svg>
  );
}
